import type { Expense } from "@/lib/domains";
import { useExpenses } from "@/contexts/expense-context";
import { formatCurrency } from "@/lib/utils/currency";
import { Trash2, Pencil, MoreVertical, Copy, Repeat } from "lucide-react";
import { ConfirmDialog } from "./confirm-dialog";
import { ExpenseEditModal } from "./expense-edit-modal";
import { useCategoriesQuery } from "@/hooks/useCategoriesQuery";
import { useExpenseCardState } from "./use-expense-card-state";
import { memo, useMemo } from "react";

interface ExpenseCardProps {
    expense: Expense;
}

/**
 * ExpenseCard component - displays a single expense with actions.
 * Memoized to prevent unnecessary re-renders when parent updates but expense data hasn't changed.
 */
function ExpenseCardComponent({ expense }: ExpenseCardProps) {
    const { deleteExpense, addExpense } = useExpenses();

    // Refactored: Using state machine instead of 4 separate useState calls
    const { state, actions, menuRef } = useExpenseCardState();

    // Use cached categories from React Query to prevent color flash
    const { data: categories = [] } = useCategoriesQuery();
    const category = useMemo(
        () => categories.find(cat => cat.id === expense.categoryId),
        [categories, expense.categoryId]
    );

    const handleDelete = async () => {
        actions.startDeleting();
        try {
            await deleteExpense(expense.id);
            actions.close();
        } catch {
            // Error is handled by the context
        }
    };

    const handleDuplicate = async () => {
        actions.close();
        try {
            await addExpense({
                amount: expense.amount,
                currency: expense.currency,
                date: new Date().toISOString().split("T")[0], // Today's date
                category: expense.category,
                categoryId: expense.categoryId,
                description: expense.description,
            });
        } catch (error) {
            console.error("Failed to duplicate expense:", error);
        }
    };

    const formattedDate = new Date(expense.date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

    // Check if expense has been edited
    const isEdited = expense.updatedAt && expense.createdAt !== expense.updatedAt;

    return (
        <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                    <div className="flex-1">
                        <div className="flex items-baseline gap-2 mb-1">
                            <span className="text-2xl font-mono font-semibold text-gray-900">
                                {formatCurrency(expense.amount, expense.currency)}
                            </span>
                            {category ? (
                                <span
                                    className="text-sm font-medium text-white px-3 py-1 rounded-full"
                                    style={{ backgroundColor: category.color }}
                                >
                                    {category.icon} {category.displayName}
                                </span>
                            ) : (
                                <span className="text-sm font-medium text-white bg-teal-600 px-3 py-1 rounded-full">
                                    {expense.category}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-600 mb-1">
                            {formattedDate}
                            {expense.isAutoGenerated && (
                                <span className="ml-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded inline-flex items-center gap-1">
                                    <Repeat className="w-3 h-3" />
                                    Auto
                                </span>
                            )}
                            {isEdited && (
                                <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    Edited
                                </span>
                            )}
                        </p>
                        {expense.description && (
                            <p className="text-sm text-gray-500">{expense.description}</p>
                        )}
                    </div>
                    <div className="relative flex gap-2 ml-4" ref={menuRef}>
                        <button
                            onClick={() => state.type === 'menu' ? actions.close() : actions.openMenu()}
                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="More options"
                        >
                            <MoreVertical size={20} />
                        </button>

                        {/* Dropdown Menu */}
                        {state.type === 'menu' && (
                            <div className="absolute right-0 top-10 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10 w-48">
                                <button
                                    onClick={handleDuplicate}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Copy size={16} />
                                    Duplicate
                                </button>
                                <button
                                    onClick={() => {
                                        actions.close();
                                        actions.openEdit();
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                    <Pencil size={16} />
                                    Edit
                                </button>
                                <div className="border-t border-gray-100 my-1"></div>
                                <button
                                    onClick={() => {
                                        actions.close();
                                        actions.openConfirmDelete();
                                    }}
                                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                    <Trash2 size={16} />
                                    Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <ExpenseEditModal
                expense={expense}
                isOpen={state.type === 'edit'}
                onClose={actions.close}
            />

            <ConfirmDialog
                isOpen={state.type === 'confirm-delete'}
                title="Delete Expense?"
                message="This expense will be permanently deleted."
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={handleDelete}
                onCancel={actions.close}
                isDestructive
                isLoading={state.type === 'confirm-delete' && state.isDeleting}
            />
        </>
    );
}

/**
 * Memoized version prevents re-renders when expense data hasn't changed.
 * Critical for performance in lists with many expenses.
 */
export const ExpenseCard = memo(ExpenseCardComponent);
